import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Logo } from "@/components/Logo";
import { ArrowRight, BookOpen, Calendar, Tag } from "lucide-react";
import type { BlogPost } from "@shared/schema";
import { getBlogPosts } from "@/lib/blogFirestore";

const CATEGORIES = ["All", "App Updates", "News & Events", "Business Tips", "General"];

const CATEGORY_COLORS: Record<string, string> = {
  "App Updates": "bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400",
  "News & Events": "bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400",
  "Business Tips": "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400",
  "General": "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

const CATEGORY_GRADIENTS: Record<string, string> = {
  "App Updates": "from-blue-500 to-blue-700",
  "News & Events": "from-green-500 to-emerald-700",
  "Business Tips": "from-amber-500 to-orange-600",
  "General": "from-slate-400 to-slate-600",
};

function formatDate(dateStr: string | Date | null) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function Blogs() {
  const [activeCategory, setActiveCategory] = useState("All");

  const { data: posts = [], isLoading } = useQuery<BlogPost[]>({
    queryKey: ["firestore/blog_posts/published"],
    queryFn: () => getBlogPosts(true),
  });

  const filtered = activeCategory === "All"
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      <header className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-[#8B2121]/10 dark:border-white/10 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Logo size="md" />
          <Link
            href="/blogs/admin"
            className="text-xs text-slate-400 dark:text-slate-500 hover:text-[#8B2121] transition-colors"
          >
            Admin
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <BookOpen className="h-5 w-5 text-[#8B2121]" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">DailyHisaab Blog</h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Tips, updates and insights for Indian small businesses</p>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              data-testid={`filter-category-${cat}`}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-bold border transition-colors ${
                activeCategory === cat
                  ? "text-white border-[#8B2121]"
                  : "bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-700"
              }`}
              style={activeCategory === cat ? { background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" } : {}}
            >
              {cat}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white dark:bg-slate-900 rounded-3xl h-40 animate-pulse border border-slate-100 dark:border-slate-800" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No posts yet in this category.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((post) => (
              <Link key={post.id} href={`/blogs/${post.slug}`}>
                <div
                  data-testid={`card-post-${post.id}`}
                  className="bg-white dark:bg-slate-900 rounded-3xl overflow-hidden shadow-sm border border-slate-100 dark:border-slate-800 hover:shadow-md transition-shadow cursor-pointer"
                >
                  {post.coverImageUrl ? (
                    <img src={post.coverImageUrl} alt={post.title} className="w-full h-44 object-cover" />
                  ) : (
                    <div className={`h-28 bg-gradient-to-br ${CATEGORY_GRADIENTS[post.category || "General"] || CATEGORY_GRADIENTS["General"]} flex items-center justify-center`}>
                      <BookOpen className="h-8 w-8 text-white/60" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[post.category || "General"] || CATEGORY_COLORS["General"]}`}>
                        <Tag className="h-3 w-3 inline mr-1" />
                        {post.category || "General"}
                      </span>
                      {post.createdAt && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.createdAt)}
                        </span>
                      )}
                    </div>
                    <h2 className="font-bold text-slate-900 dark:text-white text-base mb-1 line-clamp-2">{post.title}</h2>
                    {post.excerpt && (
                      <p className="text-slate-500 dark:text-slate-400 text-sm line-clamp-2">{post.excerpt}</p>
                    )}
                    <div className="flex items-center gap-1 mt-3 text-[#8B2121] text-sm font-bold">
                      Read more <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
